/**
 * Receipts ("Belege") data hooks.
 *
 * Upload attaches a file (optionally to a transaction in one step) and returns
 * both the stored `Receipt` and any match `candidates` the server found for an
 * unassigned upload. Attach/detach/delete all move `receipt_count` on the
 * transaction list and the `receipts` array on the transaction detail/inbox
 * item, so — like every other transaction-touching mutation in this app (see
 * `useTransactions.ts::useInvalidateTransactionViews`) — they invalidate the
 * full `['transactions']` + `['inbox']` + `['reports']` + `['budgets']` set in
 * addition to the `['receipts']` family. `useInvalidateTransactionViews` itself
 * is a module-private helper in `useTransactions.ts` (not exported), so the
 * same key set is mirrored locally here rather than imported.
 *
 * `guardOnline()` is NOT called inside these hooks — like every other mutation
 * hook in this app, that's the call-site's responsibility (see `useImport.ts`
 * and its callers in `pages/import`).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk, type ReceiptStatusFilter } from '../queryKeys.ts';
import type {
  Money,
  Receipt,
  ReceiptCandidate,
  ReceiptSettings,
  ReceiptUploadResult,
} from '../types.ts';

/** Wire shape of `GET /api/receipts` — `data` is `{ items: Receipt[] }`, not a bare array. */
interface ReceiptListOut {
  items: Receipt[];
}

// ── Queries ────────────────────────────────────────────────────────────────

/** GET /api/receipts?status= — unassigned / attached / all receipts. */
export function useReceipts(status: ReceiptStatusFilter): UseQueryResult<Receipt[]> {
  return useQuery({
    queryKey: qk.receiptsList(status),
    queryFn: async () => {
      const { items } = await api.get<ReceiptListOut>(`/api/receipts?status=${status}`);
      return items;
    },
  });
}

/** GET /api/receipts/{id}/candidates?amount= — skips while `receiptId` is null. */
export function useReceiptCandidates(
  receiptId: number | null,
  amount?: Money,
): UseQueryResult<ReceiptCandidate[]> {
  return useQuery({
    queryKey:
      receiptId === null
        ? (['receipts', 'candidates', 'disabled'] as const)
        : qk.receiptCandidates(receiptId, amount),
    queryFn: () => {
      const params = new URLSearchParams();
      if (amount) params.set('amount', amount);
      const qs = params.toString();
      return api.get<ReceiptCandidate[]>(
        `/api/receipts/${receiptId}/candidates${qs ? `?${qs}` : ''}`,
      );
    },
    enabled: receiptId !== null,
  });
}

/** GET /api/admin/receipt-settings. */
export function useReceiptSettings(): UseQueryResult<ReceiptSettings> {
  return useQuery({
    queryKey: qk.receiptSettings,
    queryFn: () => api.get<ReceiptSettings>('/api/admin/receipt-settings'),
  });
}

// ── Invalidation ─────────────────────────────────────────────────────────────

/** Mirrors `useTransactions.ts::useInvalidateTransactionViews` — every view a
 * receipt attach/detach/upload/delete can affect (`receipt_count`, the detail
 * `receipts` array) — plus the `['receipts']` family itself. */
function invalidateReceiptViews(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: qk.receipts });
  void qc.invalidateQueries({ queryKey: qk.transactions });
  void qc.invalidateQueries({ queryKey: qk.inbox });
  void qc.invalidateQueries({ queryKey: qk.reports });
  void qc.invalidateQueries({ queryKey: qk.budgets });
}

// ── Mutations ──────────────────────────────────────────────────────────────

export interface UploadReceiptArgs {
  file: File;
  /** Attach in the same call; omit to upload unassigned (candidates returned instead). */
  transactionId?: number;
}

/** POST /api/receipts (multipart) — stores the file and either attaches it or
 * returns match candidates. */
export function useUploadReceipt(): UseMutationResult<
  ReceiptUploadResult,
  Error,
  UploadReceiptArgs
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, transactionId }: UploadReceiptArgs) => {
      const form = new FormData();
      form.append('file', file);
      if (transactionId !== undefined) form.append('transaction_id', String(transactionId));
      return api.postForm<ReceiptUploadResult>('/api/receipts', form);
    },
    onSuccess: () => invalidateReceiptViews(qc),
  });
}

export interface AttachReceiptVars {
  id: number;
  transactionId: number;
}

/** POST /api/receipts/{id}/attach — links an unassigned receipt to a transaction. */
export function useAttachReceipt(): UseMutationResult<Receipt, Error, AttachReceiptVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, transactionId }: AttachReceiptVars) =>
      api.post<Receipt>(`/api/receipts/${id}/attach`, { transaction_id: transactionId }),
    onSuccess: () => invalidateReceiptViews(qc),
  });
}

/** POST /api/receipts/{id}/detach — unlinks a receipt, leaving it unassigned. */
export function useDetachReceipt(): UseMutationResult<Receipt, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Receipt>(`/api/receipts/${id}/detach`),
    onSuccess: () => invalidateReceiptViews(qc),
  });
}

/** DELETE /api/receipts/{id}. */
export function useDeleteReceipt(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/api/receipts/${id}`),
    onSuccess: () => invalidateReceiptViews(qc),
  });
}

/** PUT /api/admin/receipt-settings — switch the extraction engine. */
export function useUpdateReceiptSettings(): UseMutationResult<
  ReceiptSettings,
  Error,
  ReceiptSettings['extraction_engine']
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (extraction_engine: ReceiptSettings['extraction_engine']) =>
      api.put<ReceiptSettings>('/api/admin/receipt-settings', { extraction_engine }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.receiptSettings });
    },
  });
}
